import React from 'react';
import { Wrench, AlertCircle, CheckCircle, Clock } from 'lucide-react';

const RetailFacilities: React.FC = () => {
  const tickets = [
    {
      id: 'RTKT-001',
      type: 'HVAC',
      issue: 'Temperature too warm in fitting rooms',
      status: 'IN_PROGRESS',
      priority: 'HIGH',
      assignedTo: 'HVAC Tech Team',
      created: '2 hours ago'
    },
    {
      id: 'RTKT-002',
      type: 'Lighting',
      issue: 'Spotlight out in window display',
      status: 'OPEN',
      priority: 'HIGH',
      assignedTo: 'Unassigned',
      created: '4 hours ago'
    },
    {
      id: 'RTKT-003',
      type: 'Cleaning',
      issue: 'Restroom requires attention',
      status: 'COMPLETED',
      priority: 'MEDIUM',
      assignedTo: 'Janitorial',
      created: '1 day ago'
    },
    {
      id: 'RTKT-004',
      type: 'Equipment',
      issue: 'Register 3 receipt printer jammed',
      status: 'IN_PROGRESS',
      priority: 'MEDIUM',
      assignedTo: 'IT Support',
      created: '30 minutes ago'
    }
  ];

  const metrics = [
    { label: 'Open Tickets', value: '4', icon: AlertCircle, color: '#f59e0b' },
    { label: 'In Progress', value: '2', icon: Clock, color: 'var(--sca-teal)' },
    { label: 'Resolved Today', value: '8', icon: CheckCircle, color: 'var(--sca-teal)' },
    { label: 'Avg. Response', value: '12m', icon: Clock, color: 'var(--sca-teal)' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'var(--sca-teal)';
      case 'IN_PROGRESS': return '#f59e0b';
      case 'OPEN': return '#ef4444';
      default: return 'var(--sca-muted)';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return '#ef4444';
      case 'MEDIUM': return '#f59e0b';
      case 'LOW': return 'var(--sca-teal)';
      default: return 'var(--sca-muted)';
    }
  };

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Facility Management
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Maintain store operations and manage maintenance requests
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, idx) => (
          <div key={idx} className="card">
            <metric.icon size={24} className="mb-2" style={{ color: metric.color }} />
            <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
              {metric.value}
            </div>
            <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
              {metric.label}
            </div>
          </div>
        ))}
      </div>

      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--sca-ink)' }}>
            Active Tickets
          </h3>
          <button
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: 'var(--sca-teal)',
              color: 'white',
              border: 'none'
            }}
          >
            Create Ticket
          </button>
        </div>

        <div className="space-y-3">
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              className="p-4 rounded-lg"
              style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-mono" style={{ color: 'var(--sca-muted)' }}>
                      {ticket.id}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${getStatusColor(ticket.status)}20`,
                        color: getStatusColor(ticket.status)
                      }}
                    >
                      {ticket.status.replace('_', ' ')}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${getPriorityColor(ticket.priority)}20`,
                        color: getPriorityColor(ticket.priority)
                      }}
                    >
                      {ticket.priority}
                    </span>
                  </div>
                  <div className="font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                    {ticket.type}: {ticket.issue}
                  </div>
                  <div className="flex gap-4 text-xs" style={{ color: 'var(--sca-muted)' }}>
                    <span>Assigned: {ticket.assignedTo}</span>
                    <span>Created: {ticket.created}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Preventive Maintenance
          </h3>
          <div className="space-y-3">
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                HVAC Filter Replacement
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                Due in 5 days
              </div>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)' }}>
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                Fire Safety Inspection
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                Due in 12 days
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Quick Actions
          </h3>
          <div className="space-y-2">
            <button
              className="w-full p-3 rounded-lg text-left text-sm transition-colors"
              style={{
                border: '1px solid var(--sca-border)',
                backgroundColor: 'transparent',
                color: 'var(--sca-ink)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sca-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Report Equipment Issue
            </button>
            <button
              className="w-full p-3 rounded-lg text-left text-sm transition-colors"
              style={{
                border: '1px solid var(--sca-border)',
                backgroundColor: 'transparent',
                color: 'var(--sca-ink)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sca-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Schedule Cleaning
            </button>
            <button
              className="w-full p-3 rounded-lg text-left text-sm transition-colors"
              style={{
                border: '1px solid var(--sca-border)',
                backgroundColor: 'transparent',
                color: 'var(--sca-ink)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--sca-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Request Supplies
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetailFacilities;
